// Shapes
import { Authorities } from "~shapes/auth"
import { RolesShape } from "~shapes/role"

export interface RolesKeyShape {
  [key: string]: string
}

export enum ROLES {
  ROLE_USER = "ROLE_USER",
  ROLE_ADMIN = "ROLE_ADMIN",
  ROLE_PERK_CENTRAL_USER = "ROLE_PERK_CENTRAL_USER",
  ROLE_PERK_CENTRAL_COMPANY_ADMIN = "ROLE_PERK_CENTRAL_COMPANY_ADMIN",
  ROLE_PERK_CENTRAL_USER_CCL_SEABOURN = "ROLE_PERK_CENTRAL_USER_CCL_SEABOURN",
  ROLE_PERK_CENTRAL_USER_CCL_PRINCESS = "ROLE_PERK_CENTRAL_USER_CCL_PRINCESS",
  ROLE_PERK_CENTRAL_USER_CCL_PO_CRUISES_AUS = "ROLE_PERK_CENTRAL_USER_CCL_PO_CRUISES_AUS",
  ROLE_PERK_CENTRAL_USER_CCL_PO_CRUISES_UK = "ROLE_PERK_CENTRAL_USER_CCL_PO_CRUISES_UK",
  ROLE_PERK_CENTRAL_USER_CCL_HOLLAND_AMERICA_LINE = "ROLE_PERK_CENTRAL_USER_CCL_HOLLAND_AMERICA_LINE",
  ROLE_PERK_CENTRAL_USER_CCL_CUNARD = "ROLE_PERK_CENTRAL_USER_CCL_CUNARD",
  ROLE_PERK_CENTRAL_USER_CCL_COSTA = "ROLE_PERK_CENTRAL_USER_CCL_COSTA",
  ROLE_PERK_CENTRAL_USER_CCL_AIDA = "ROLE_PERK_CENTRAL_USER_CCL_AIDA",
  ROLE_PERK_CENTRAL_USER_CCL_CARNIVAL_CRUISE_LINE = "ROLE_PERK_CENTRAL_USER_CCL_CARNIVAL_CRUISE_LINE",
  ROLE_CUSTOMER_SUPPORT = "ROLE_CUSTOMER_SUPPORT",
}

export enum ROLES_USER_FACING {
  ROLE_USER = "User",
  ROLE_ADMIN = "Admin",
  ROLE_PERK_CENTRAL_USER = "Company User",
  ROLE_PERK_CENTRAL_COMPANY_ADMIN = "Company Admin",
  ROLE_PERK_CENTRAL_USER_CCL_SEABOURN = "CCL Seabourn",
  ROLE_PERK_CENTRAL_USER_CCL_PRINCESS = "CCL Princess",
  ROLE_PERK_CENTRAL_USER_CCL_PO_CRUISES_AUS = "CCL P&O Australia",
  ROLE_PERK_CENTRAL_USER_CCL_PO_CRUISES_UK = "CCL P&O UK",
  ROLE_PERK_CENTRAL_USER_CCL_HOLLAND_AMERICA_LINE = "CCL HAL",
  ROLE_PERK_CENTRAL_USER_CCL_CUNARD = "CCL Cunard",
  ROLE_PERK_CENTRAL_USER_CCL_COSTA = "CCL Costa",
  ROLE_PERK_CENTRAL_USER_CCL_AIDA = "CCL Aida",
  ROLE_PERK_CENTRAL_USER_CCL_CARNIVAL_CRUISE_LINE = "CCL Carnival Cruise Line",
  ROLE_CUSTOMER_SUPPORT = "Customer Support",
}

const ROLES_HIERARCHY = {
  ROLE_USER: {
    weight: 1,
    children: [],
  },
  ROLE_PERK_CENTRAL_USER: {
    weight: 2,
    children: ["ROLE_USER"],
  },
  ROLE_PERK_CENTRAL_USER_CCL_SEABOURN: {
    weight: 3,
    children: ["ROLE_USER"],
  },
  ROLE_PERK_CENTRAL_USER_CCL_PO_CRUISES_AUS: {
    weight: 3,
    children: ["ROLE_USER"],
  },
  ROLE_PERK_CENTRAL_USER_CCL_PO_CRUISES_UK: {
    weight: 3,
    children: ["ROLE_USER"],
  },
  ROLE_PERK_CENTRAL_USER_CCL_HOLLAND_AMERICA_LINE: {
    weight: 3,
    children: ["ROLE_USER"],
  },
  ROLE_PERK_CENTRAL_USER_CCL_CUNARD: {
    weight: 3,
    children: ["ROLE_USER"],
  },
  ROLE_PERK_CENTRAL_USER_CCL_CARNIVAL_CRUISE_LINE: {
    weight: 3,
    children: ["ROLE_USER"],
  },
  ROLE_PERK_CENTRAL_USER_CCL_PRINCESS: {
    weight: 3,
    children: ["ROLE_USER"],
  },
  ROLE_PERK_CENTRAL_USER_CCL_AIDA: {
    weight: 3,
    children: ["ROLE_USER"],
  },
  ROLE_PERK_CENTRAL_USER_CCL_COSTA: {
    weight: 3,
    children: ["ROLE_USER"],
  },
  ROLE_CUSTOMER_SUPPORT: {
    weight: 3,
    children: ["ROLE_USER"],
  },
  ROLE_PERK_CENTRAL_COMPANY_ADMIN: {
    weight: 4,
    children: ["ROLE_USER", "ROLE_PERK_CENTRAL_USER"],
  },
  ROLE_ADMIN: {
    weight: 10,
    children: ["ROLE_USER"],
  },
}

export const GetRoleKeys = (): RolesKeyShape => {
  return Object.keys(ROLES).reduce((acc, key) => {
    acc[key] = acc[key] || key
    return acc
  }, {})
}

export const GetAssignableRoles = (roles: RolesShape[]): RolesShape[] => {
  // Get roles keys
  const ROLE_KEYS = GetRoleKeys()

  // Remove roles that we don't want users to be able to assign
  return roles.filter(role => {
    switch (role.name) {
      // Remove ADMIN, no one can assign those roles
      case ROLE_KEYS.ROLE_ADMIN:
        return false

      default:
        return true
    }
  })
}

export const GetHighestRoles = (roles: RolesShape[]): RolesShape[] => {
  // Reduce all the roles by weight
  const reducedRoles = roles.reduce(
    (acc: { [key: number]: RolesShape[] }, role: RolesShape) => {
      const roleWeight = ROLES_HIERARCHY[role.name].weight

      acc[roleWeight] = acc[roleWeight] ? [...acc[roleWeight], role] : [role]

      return acc
    },
    {}
  )

  // Get highest weight
  const weights = Object.keys(reducedRoles)
  const highestWeight = weights[weights.length - 1]

  return reducedRoles[highestWeight]
}

export const GetHighestAuthorities = (authorities: Authorities): string => {
  // Reduce all the roles by weight
  const reducedRoles = authorities.roles.reduce(
    (acc: { [key: number]: string }, authority: string) => {
      const roleWeight = ROLES_HIERARCHY[authority]?.weight || 0

      acc[roleWeight] = acc[roleWeight]
        ? acc[roleWeight]
        : ROLES_USER_FACING[authority]

      return acc
    },
    {}
  )

  // Get highest weight
  const weights = Object.keys(reducedRoles)
  const highestWeight = weights[weights.length - 1]

  return reducedRoles[highestWeight]
}

export const GetRoleChildren = (
  selectedRoles: string[],
  allRoles: RolesShape[]
): RolesShape[] => {
  // Reduce roles to decrease algorithm complexity
  const reducedRoles = allRoles.reduce(
    (acc: { [key: string]: RolesShape }, role: RolesShape) => {
      acc[role.name] = role
      return acc
    },
    {}
  )

  // Reduce all the roles to it's children
  const reducedAllRoles = selectedRoles.reduce(
    (acc: { [key: string]: RolesShape }, roleName: string) => {
      // Add parent
      acc[roleName] = reducedRoles[roleName]

      // Add all children
      ROLES_HIERARCHY[roleName]?.children.forEach((name: string) => {
        if (!acc[name]) acc[name] = reducedRoles[name]
      })

      return acc
    },
    {}
  )

  return Object.values(reducedAllRoles)
}
